import React, { useState, useEffect } from 'react';
import SensitiveOff from '../../assets/sensitive-off.svg';
import SensitiveOn from '../../assets/sensitive-on.svg';
import BotIcon from '../../assets/bot-icon.svg';
import ModeratorIcon from '../../assets/moderator-icon.svg';
import CommunityIcon from '../../assets/community-icon.svg';
import { MODERATION_OPTIONS, FORMATTED_VIOLATIONS } from '../../constants/article.constant';
import { userSettingsApi } from '../../apis/user-settings.api';

const SensitiveContentCard = ({
  isComment,
  primarySource,
  primaryViolation,
  actionButton,
  setShowSensitiveContentCard,
  articleId,
  commentId,
  isShowSensitive
}) => {
  const [userSettings, setUserSettings] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get user settings from localStorage
  useEffect(() => {
    const getUserSettings = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          const user = JSON.parse(userData);
          if (isComment && commentId) {
            // For comments, check comment-specific settings
            setUserSettings(user.commentSettings?.[commentId] || null);
          } else if (articleId) {
            // For articles, check article-specific settings
            setUserSettings(user.articleSettings?.[articleId] || null);
          }
        }
      } catch (error) {
        console.error('Failed to get user settings from localStorage:', error);
      } finally {
        setIsLoading(false);
      }
    };

    getUserSettings();
  }, [isComment, commentId, articleId]);

  // If user has enabled showing sensitive content for this article, don't render the card
  if (!isLoading && isShowSensitive) {
    return null;
  }

  // Create a wrapper function that detects when the card is being dismissed
  const wrappedSetShowSensitiveContentCard = (value) => {
    // If we're hiding the card (value = false) and user hasn't enabled showing sensitive content yet
    if (value === false && !isShowSensitive) {
      // Update localStorage immediately for better UX
      const userData = localStorage.getItem('user');
      if (userData) {
        const user = JSON.parse(userData);

        if (isComment && commentId && articleId) {
          // For comments, we need both articleId and commentId
          // Update comment-specific setting in localStorage
          if (!user.commentSettings) {
            user.commentSettings = {};
          }
          user.commentSettings[commentId] = {
            showSensitiveContent: true
          };
          localStorage.setItem('user', JSON.stringify(user));
          setUserSettings(user.commentSettings[commentId]);

          // Update via API to persist on server with both articleId and commentId
          userSettingsApi
            .updateUserSettings({
              showSensitiveContent: true,
              articleId: articleId,
              commentId: commentId
            })
            .catch((error) => {
              console.error('Failed to update user settings via API:', error);
            });
        } else if (articleId) {
          // Update article-specific setting in localStorage
          if (!user.articleSettings) {
            user.articleSettings = {};
          }
          user.articleSettings[articleId] = {
            showSensitiveContent: true
          };
          localStorage.setItem('user', JSON.stringify(user));
          setUserSettings(user.articleSettings[articleId]);

          // Update via API to persist on server with articleId and null commentId
          userSettingsApi
            .updateUserSettings({
              showSensitiveContent: true,
              articleId: articleId,
              commentId: null // null for articles
            })
            .catch((error) => {
              console.error('Failed to update user settings via API:', error);
            });
        }
      }
    }
    // Call the original function
    setShowSensitiveContentCard(value);
  };

  const showIcon = () => {
    switch (primarySource) {
      case MODERATION_OPTIONS.AI:
        return BotIcon;
      case MODERATION_OPTIONS.MODERATOR:
        return ModeratorIcon;
      case MODERATION_OPTIONS.USER_REPORTS:
        return CommunityIcon;
      default:
        return null;
    }
  };

  const flaggedBy = () => {
    switch (primarySource) {
      case MODERATION_OPTIONS.AI:
        return 'our content safety system';
      case MODERATION_OPTIONS.MODERATOR:
        return 'our moderation team';
      case MODERATION_OPTIONS.USER_REPORTS:
        return 'multiple community members';
      default:
        return null;
    }
  };

  const renderPrimaryViolationTextBy = () => {
    switch (primarySource) {
      case MODERATION_OPTIONS.AI:
        return (
          <p>
            for potentially containing{' '}
            <span className="font-[800]">{FORMATTED_VIOLATIONS[primaryViolation]}</span>.
          </p>
        );
      case MODERATION_OPTIONS.MODERATOR:
        return (
          <p>
            for containing{' '}
            <span className="font-[800]">{FORMATTED_VIOLATIONS[primaryViolation]}</span>.
          </p>
        );
      case MODERATION_OPTIONS.USER_REPORTS:
        return (
          <p>
            as potentially containing{' '}
            <span className="font-[800]">{FORMATTED_VIOLATIONS[primaryViolation]}</span>.
          </p>
        );
      default:
        return null;
    }
  };

  const handleShowAnyway = async () => {
    // Use the wrapper function which will automatically update user settings
    wrappedSetShowSensitiveContentCard(false);
  };

  return (
    <div className="py-[10px] px-10 backdrop-blur-[4px] bg-[#FFFFFFB2] w-[308px] h-fit border border-[#CCCCCC4D] rounded-[20px]">
      <div className="flex flex-col gap-1 items-center justify-center">
        {isComment || <img src={SensitiveOff} alt="" />}
        {isComment || <p className="text-sm font-[800]">Sensitive Content</p>}
        <div className="flex flex-col text-xs text-center font-[600]">
          <span>This content has been flagged by</span>
          <div className="font-[800] flex gap-1 items-center justify-center">
            {flaggedBy()} <img src={showIcon()} alt="" />
          </div>
          {renderPrimaryViolationTextBy()}
        </div>
      </div>
      <div
        className="flex justify-center text-xs font-[800] py-[2px] mt-2 gap-1 items-center cursor-pointer hover:opacity-80"
        onClick={handleShowAnyway}
      >
        {actionButton}
        <img src={SensitiveOn} alt="" />
      </div>
    </div>
  );
};

export default SensitiveContentCard;
